import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';
import logger from '@/app/utils/logger.ts';
import { checkIsOutdated, getOutdatedState } from '@/app/utils/buildVersion.ts';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isOutdated: boolean;
}

const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  boxSizing: 'border-box',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  background: '#f5f5f5',
  color: '#1f1f1f'
};

const cardStyle: React.CSSProperties = {
  maxWidth: '480px',
  width: '100%',
  padding: '32px',
  borderRadius: '12px',
  background: '#ffffff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  textAlign: 'center'
};

const primaryTextStyle: React.CSSProperties = { margin: '0 0 8px', fontSize: '1.125rem', fontWeight: 600 };
const secondaryTextStyle: React.CSSProperties = { margin: '0 0 24px', fontSize: '0.9375rem', color: '#555555' };

const buttonStyle: React.CSSProperties = {
  padding: '10px 24px',
  border: 'none',
  borderRadius: '8px',
  background: '#1976d2',
  color: '#ffffff',
  fontSize: '0.9375rem',
  fontWeight: 600,
  cursor: 'pointer'
};

/**
 * Outermost error boundary, so a crash anywhere in the tree (header, providers, ...) shows a
 * reload screen instead of a white page.
 *
 * Deliberately free of MUI/i18n/redux/router: those providers may be what crashed. Text is
 * hardcoded English with a Norwegian line underneath.
 *
 * Known limitation: it cannot catch errors thrown before `root.render` in index.tsx (e.g. the MSAL
 * initialization there runs outside of it).
 */
export class RootErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isOutdated: false
  };

  private unmounted = false;

  public static getDerivedStateFromError(): Partial<State> {
    // Seed isOutdated synchronously so a tab already known to be outdated never depends on the
    // async check in componentDidCatch.
    return { hasError: true, isOutdated: getOutdatedState().outdated };
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(error, { componentStack: errorInfo.componentStack, boundary: 'root' });

    void checkIsOutdated().then((isOutdated) => {
      if (isOutdated && !this.unmounted) {
        this.setState({ isOutdated: true });
      }
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const [english, norwegian] = this.state.isOutdated
      ? [
          'A new version of SKLLS is available. Reload to continue.',
          'En ny versjon av SKLLS er tilgjengelig. Last inn siden på nytt for å fortsette.'
        ]
      : ['Something went wrong.', 'Noe gikk galt.'];

    return (
      <div style={containerStyle} role="alert">
        <div style={cardStyle}>
          <p style={primaryTextStyle}>{english}</p>
          <p style={secondaryTextStyle} lang="nb">
            {norwegian}
          </p>
          <button type="button" style={buttonStyle} onClick={this.handleReload}>
            Reload / Last inn på nytt
          </button>
        </div>
      </div>
    );
  }
}

export default RootErrorBoundary;
